import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import toast from 'react-hot-toast'
import { useTemporaryStore } from '@/contexts/temporaryStore'
import { billing } from '@/services/billing/billing-service'
import { Divider } from '@heroui/react'
import { BillingModalCopy, ProductMetadata } from '@interfaces/pricing'

// Default values for product metadata
const DEFAULT_TRIAL_DOCS_CAP = 3
const DEFAULT_TRIAL_EXPORT_CAP = 2
const DEFAULT_TRIAL_AI_IMPROVEMENT_CAP = 6
const DEFAULT_TRIAL_REVIEWS_CAP = 5
const DEFAULT_PAID_EXPORT_CAP = 20
const DEFAULT_MAX_SIGNUPS_PER_IP = 2
const DEFAULT_SIGNUP_RATE_LIMIT_WINDOW = 5

const defaultCopy: BillingModalCopy = {
  TRIAL_LIMIT_EXPORT_COUNT: {
    title: 'Ready to send your document?',
    body: "You've used your %trialExportCap% free exports. Upgrade to download, share, and send unlimited documents instantly.",
    ctaText: 'Upgrade to continue →',
  },
  TRIAL_LIMIT_DOCS_COUNT: {
    title: 'Create more documents with Cobrief',
    body: "You've reached your %trialDocsCap% free documents. Upgrade to continue creating without limits.",
    ctaText: 'Upgrade to continue →',
  },
  TRIAL_LIMIT_AI_IMPROVEMENTS: {
    title: 'Finish improving your document',
    body: 'Upgrade to Cobrief AI for unlimited one-click improvements and editing tools.',
    ctaText: 'Upgrade to continue →',
  },
  TRIAL_LIMIT_REVIEWS_COUNT: {
    title: "You've reached your free review limit",
    body: "You've used all %trialReviewsCap% free document reviews. Upgrade to keep reviewing, refining, and improving documents without limits.",
    ctaText: 'Upgrade to continue reviewing →',
  },
  PAID_LIMIT_EXPORT_COUNT: {
    title: 'Daily Export Limit Reached',
    body: 'You have reached your daily export limit. To ensure fair usage and system stability, we have a daily cap. Please come back tomorrow to continue exporting documents/templates.',
    ctaText: '',
  },
  NO_SUBSCRIPTION: {
    title: 'Continue with full Cobrief access',
    body: 'Upgrade to keep working without limits — editing, exports, and AI.',
    ctaText: 'Upgrade now',
  },
}

export default function ProductMetadataPage() {
  const { productMetadata, setProductMetadata } = useTemporaryStore()

  const [newMetadata, setNewMetadata] = useState<ProductMetadata>({
    trialDocsCap: DEFAULT_TRIAL_DOCS_CAP,
    trialExportCap: DEFAULT_TRIAL_EXPORT_CAP,
    trialAiImprovementCap: DEFAULT_TRIAL_AI_IMPROVEMENT_CAP,
    trialReviewsCap: DEFAULT_TRIAL_REVIEWS_CAP,
    paidExportCap: DEFAULT_PAID_EXPORT_CAP,
    maxSignupsPerIp: DEFAULT_MAX_SIGNUPS_PER_IP,
    signupRateLimitWindow: DEFAULT_SIGNUP_RATE_LIMIT_WINDOW,
    copy: defaultCopy,
  })
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (productMetadata) {
      setNewMetadata({
        ...productMetadata,
        trialDocsCap: productMetadata.trialDocsCap ?? DEFAULT_TRIAL_DOCS_CAP,
        trialExportCap: productMetadata.trialExportCap ?? DEFAULT_TRIAL_EXPORT_CAP,
        trialAiImprovementCap: productMetadata.trialAiImprovementCap ?? DEFAULT_TRIAL_AI_IMPROVEMENT_CAP,
        trialReviewsCap: productMetadata.trialReviewsCap ?? DEFAULT_TRIAL_REVIEWS_CAP,
        paidExportCap: productMetadata.paidExportCap ?? DEFAULT_PAID_EXPORT_CAP,
        maxSignupsPerIp: productMetadata.maxSignupsPerIp ?? DEFAULT_MAX_SIGNUPS_PER_IP,
        signupRateLimitWindow: productMetadata.signupRateLimitWindow ?? DEFAULT_SIGNUP_RATE_LIMIT_WINDOW,
        copy: productMetadata.copy ?? defaultCopy,
      })
    }
  }, [productMetadata])

  const handleSave = () => {
    setIsSaving(true)

    billing
      .updateActiveProductMetadata(newMetadata)
      .then(response => {
        const updatedMetadata = response.data.product.metadata
        setProductMetadata(updatedMetadata)
        toast.success('Product metadata updated successfully!')
      })
      .catch(error => {
        console.error('Error updating product metadata:', error)
        toast.error('Failed to update product metadata. Please try again.')
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  return (
    <>
      <div className="mb-8">
        <h1 className="mb-2 text-3xl font-bold">Product Metadata</h1>
        <p className="text-muted-foreground">Manage product metadata including trial limits and billing modal copy</p>
      </div>

      <section className="mb-8">
        <Card>
          <CardContent className="space-y-6 p-6">
            <div className="flex flex-wrap items-center gap-2">
              <label htmlFor="trialDocsCap" className="block font-medium">
                Trial Documents Cap
              </label>

              <p className="text-sm text-muted-foreground">
                (Maximum number of documents trial users can create or upload, global)
              </p>

              <Input
                id="trialDocsCap"
                type="number"
                min="1"
                value={newMetadata.trialDocsCap}
                className="ms-auto max-w-[10rem]"
                onChange={e => setNewMetadata({ ...newMetadata, trialDocsCap: Number(e.target.value) })}
                placeholder="Enter maximum documents for trial users"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <label htmlFor="trialExportCap" className="block font-medium">
                Trial Export Cap
              </label>

              <p className="text-sm text-muted-foreground">
                (Maximum number of times trial users can export each document, per document)
              </p>

              <Input
                id="trialExportCap"
                type="number"
                min="1"
                value={newMetadata.trialExportCap}
                className="ms-auto max-w-[10rem]"
                onChange={e => setNewMetadata({ ...newMetadata, trialExportCap: Number(e.target.value) })}
                placeholder="Enter maximum exports per document for trial users"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <label htmlFor="trialAiImprovementCap" className="block font-medium">
                Trial AI Improvement Cap
              </label>

              <p className="text-sm text-muted-foreground">
                (Maximum number of AI improvements trial users can apply, per document)
              </p>

              <Input
                id="trialAiImprovementCap"
                type="number"
                min="1"
                value={newMetadata.trialAiImprovementCap}
                className="ms-auto max-w-[10rem]"
                onChange={e => setNewMetadata({ ...newMetadata, trialAiImprovementCap: Number(e.target.value) })}
                placeholder="Enter maximum AI improvements per document for trial users"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <label htmlFor="trialReviewsCap" className="block font-medium">
                Trial Reviews Cap
              </label>

              <p className="text-sm text-muted-foreground">
                (Maximum number of reviews trial users can perform, global)
              </p>

              <Input
                id="trialReviewsCap"
                type="number"
                min="1"
                value={newMetadata.trialReviewsCap}
                className="ms-auto max-w-[10rem]"
                onChange={e => setNewMetadata({ ...newMetadata, trialReviewsCap: Number(e.target.value) })}
                placeholder="Enter maximum reviews for trial users"
              />
            </div>

            <Divider />

            <div className="flex flex-wrap items-center gap-2">
              <label htmlFor="paidExportCap" className="block font-medium">
                Paid Export Cap
              </label>

              <p className="text-sm text-muted-foreground">
                (Maximum number of times PAID users can export each document, per DAY)
              </p>

              <Input
                id="paidExportCap"
                type="number"
                min="1"
                value={newMetadata.paidExportCap}
                className="ms-auto max-w-[10rem]"
                onChange={e => setNewMetadata({ ...newMetadata, paidExportCap: Number(e.target.value) })}
                placeholder="Enter maximum exports per day for paid users"
              />
            </div>

            <Divider />

            <div className="flex flex-wrap items-center gap-2">
              <label htmlFor="maxSignupsPerIp" className="block font-medium">
                Max Signups Per IP
              </label>

              <p className="text-sm text-muted-foreground">
                (Maximum total number of accounts that can be created from a single IP address)
              </p>

              <Input
                id="maxSignupsPerIp"
                type="number"
                min="1"
                value={newMetadata.maxSignupsPerIp}
                className="ms-auto max-w-[10rem]"
                onChange={e => setNewMetadata({ ...newMetadata, maxSignupsPerIp: Number(e.target.value) })}
                placeholder="Enter max signups per IP"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <label htmlFor="signupRateLimitWindow" className="block font-medium">
                Signup Rate Limit Window (Minutes)
              </label>

              <p className="text-sm text-muted-foreground">
                (Waiting period in minutes required between signups from the same IP)
              </p>

              <Input
                id="signupRateLimitWindow"
                type="number"
                min="1"
                value={newMetadata.signupRateLimitWindow}
                className="ms-auto max-w-[10rem]"
                onChange={e => setNewMetadata({ ...newMetadata, signupRateLimitWindow: Number(e.target.value) })}
                placeholder="Enter window in minutes"
              />
            </div>
          </CardContent>
        </Card>
      </section>

      <section className="mt-8">
        <div className="mb-6">
          <h2 className="mb-2 text-2xl font-bold">Billing Modal Copy</h2>
          <p className="text-muted-foreground [&_code]:rounded-md [&_code]:bg-orange-100 [&_code]:px-1 [&_code]:py-0.5">
            Use <code>%trialExportCap%</code>, <code>%trialDocsCap%</code>, <code>%trialAiImprovementCap%</code>, and{' '}
            <code>%trialReviewsCap%</code> as placeholders.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-[1rem] md:grid-cols-2">
          {/* TRIAL_LIMIT_EXPORT_COUNT */}
          <Card>
            <CardContent className="space-y-4 p-6">
              <h3 className="text-lg font-semibold">Export Limit Modal</h3>

              <div className="space-y-2">
                <label htmlFor="exportLimit-title" className="block text-sm font-medium">
                  Title
                </label>
                <Input
                  id="exportLimit-title"
                  value={newMetadata.copy?.TRIAL_LIMIT_EXPORT_COUNT.title || ''}
                  onChange={e =>
                    setNewMetadata({
                      ...newMetadata,
                      copy: {
                        ...newMetadata.copy!,
                        TRIAL_LIMIT_EXPORT_COUNT: {
                          ...newMetadata.copy!.TRIAL_LIMIT_EXPORT_COUNT,
                          title: e.target.value,
                        },
                      },
                    })
                  }
                  placeholder="Enter modal title"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="exportLimit-body" className="block text-sm font-medium">
                  Body
                </label>
                <Input
                  id="exportLimit-body"
                  value={newMetadata.copy?.TRIAL_LIMIT_EXPORT_COUNT.body || ''}
                  onChange={e =>
                    setNewMetadata({
                      ...newMetadata,
                      copy: {
                        ...newMetadata.copy!,
                        TRIAL_LIMIT_EXPORT_COUNT: {
                          ...newMetadata.copy!.TRIAL_LIMIT_EXPORT_COUNT,
                          body: e.target.value,
                        },
                      },
                    })
                  }
                  placeholder="Enter modal body (use %trialExportCap% for dynamic values)"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="exportLimit-cta" className="block text-sm font-medium">
                  CTA Text
                </label>
                <Input
                  id="exportLimit-cta"
                  value={newMetadata.copy?.TRIAL_LIMIT_EXPORT_COUNT.ctaText || ''}
                  onChange={e =>
                    setNewMetadata({
                      ...newMetadata,
                      copy: {
                        ...newMetadata.copy!,
                        TRIAL_LIMIT_EXPORT_COUNT: {
                          ...newMetadata.copy!.TRIAL_LIMIT_EXPORT_COUNT,
                          ctaText: e.target.value,
                        },
                      },
                    })
                  }
                  placeholder="Enter CTA button text"
                />
              </div>
            </CardContent>
          </Card>

          {/* TRIAL_LIMIT_DOCS_COUNT */}
          <Card>
            <CardContent className="space-y-4 p-6">
              <h3 className="text-lg font-semibold">Documents Limit Modal</h3>

              <div className="space-y-2">
                <label htmlFor="docsLimit-title" className="block text-sm font-medium">
                  Title
                </label>
                <Input
                  id="docsLimit-title"
                  value={newMetadata.copy?.TRIAL_LIMIT_DOCS_COUNT.title || ''}
                  onChange={e =>
                    setNewMetadata({
                      ...newMetadata,
                      copy: {
                        ...newMetadata.copy!,
                        TRIAL_LIMIT_DOCS_COUNT: {
                          ...newMetadata.copy!.TRIAL_LIMIT_DOCS_COUNT,
                          title: e.target.value,
                        },
                      },
                    })
                  }
                  placeholder="Enter modal title"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="docsLimit-body" className="block text-sm font-medium">
                  Body
                </label>
                <Input
                  id="docsLimit-body"
                  value={newMetadata.copy?.TRIAL_LIMIT_DOCS_COUNT.body || ''}
                  onChange={e =>
                    setNewMetadata({
                      ...newMetadata,
                      copy: {
                        ...newMetadata.copy!,
                        TRIAL_LIMIT_DOCS_COUNT: {
                          ...newMetadata.copy!.TRIAL_LIMIT_DOCS_COUNT,
                          body: e.target.value,
                        },
                      },
                    })
                  }
                  placeholder="Enter modal body (use %trialDocsCap% for dynamic values)"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="docsLimit-cta" className="block text-sm font-medium">
                  CTA Text
                </label>
                <Input
                  id="docsLimit-cta"
                  value={newMetadata.copy?.TRIAL_LIMIT_DOCS_COUNT.ctaText || ''}
                  onChange={e =>
                    setNewMetadata({
                      ...newMetadata,
                      copy: {
                        ...newMetadata.copy!,
                        TRIAL_LIMIT_DOCS_COUNT: {
                          ...newMetadata.copy!.TRIAL_LIMIT_DOCS_COUNT,
                          ctaText: e.target.value,
                        },
                      },
                    })
                  }
                  placeholder="Enter CTA button text"
                />
              </div>
            </CardContent>
          </Card>

          {/* TRIAL_LIMIT_AI_IMPROVEMENTS */}
          <Card>
            <CardContent className="space-y-4 p-6">
              <h3 className="text-lg font-semibold">AI Improvements Limit Modal</h3>

              <div className="space-y-2">
                <label htmlFor="aiLimit-title" className="block text-sm font-medium">
                  Title
                </label>
                <Input
                  id="aiLimit-title"
                  value={newMetadata.copy?.TRIAL_LIMIT_AI_IMPROVEMENTS.title || ''}
                  onChange={e =>
                    setNewMetadata({
                      ...newMetadata,
                      copy: {
                        ...newMetadata.copy!,
                        TRIAL_LIMIT_AI_IMPROVEMENTS: {
                          ...newMetadata.copy!.TRIAL_LIMIT_AI_IMPROVEMENTS,
                          title: e.target.value,
                        },
                      },
                    })
                  }
                  placeholder="Enter modal title"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="aiLimit-body" className="block text-sm font-medium">
                  Body
                </label>
                <Input
                  id="aiLimit-body"
                  value={newMetadata.copy?.TRIAL_LIMIT_AI_IMPROVEMENTS.body || ''}
                  onChange={e =>
                    setNewMetadata({
                      ...newMetadata,
                      copy: {
                        ...newMetadata.copy!,
                        TRIAL_LIMIT_AI_IMPROVEMENTS: {
                          ...newMetadata.copy!.TRIAL_LIMIT_AI_IMPROVEMENTS,
                          body: e.target.value,
                        },
                      },
                    })
                  }
                  placeholder="Enter modal body (use %trialAiImprovementCap% for dynamic values)"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="aiLimit-cta" className="block text-sm font-medium">
                  CTA Text
                </label>
                <Input
                  id="aiLimit-cta"
                  value={newMetadata.copy?.TRIAL_LIMIT_AI_IMPROVEMENTS.ctaText || ''}
                  onChange={e =>
                    setNewMetadata({
                      ...newMetadata,
                      copy: {
                        ...newMetadata.copy!,
                        TRIAL_LIMIT_AI_IMPROVEMENTS: {
                          ...newMetadata.copy!.TRIAL_LIMIT_AI_IMPROVEMENTS,
                          ctaText: e.target.value,
                        },
                      },
                    })
                  }
                  placeholder="Enter CTA button text"
                />
              </div>
            </CardContent>
          </Card>

          {/* TRIAL_LIMIT_REVIEWS_COUNT */}
          <Card>
            <CardContent className="space-y-4 p-6">
              <h3 className="text-lg font-semibold">Reviews Limit Modal</h3>

              <div className="space-y-2">
                <label htmlFor="reviewsLimit-title" className="block text-sm font-medium">
                  Title
                </label>
                <Input
                  id="reviewsLimit-title"
                  value={newMetadata.copy?.TRIAL_LIMIT_REVIEWS_COUNT?.title || ''}
                  onChange={e =>
                    setNewMetadata({
                      ...newMetadata,
                      copy: {
                        ...newMetadata.copy!,
                        TRIAL_LIMIT_REVIEWS_COUNT: {
                          ...newMetadata.copy!.TRIAL_LIMIT_REVIEWS_COUNT,
                          title: e.target.value,
                        },
                      },
                    })
                  }
                  placeholder="Enter modal title"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="reviewsLimit-body" className="block text-sm font-medium">
                  Body
                </label>
                <Input
                  id="reviewsLimit-body"
                  value={newMetadata.copy?.TRIAL_LIMIT_REVIEWS_COUNT?.body || ''}
                  onChange={e =>
                    setNewMetadata({
                      ...newMetadata,
                      copy: {
                        ...newMetadata.copy!,
                        TRIAL_LIMIT_REVIEWS_COUNT: {
                          ...newMetadata.copy!.TRIAL_LIMIT_REVIEWS_COUNT,
                          body: e.target.value,
                        },
                      },
                    })
                  }
                  placeholder="Enter modal body (use %trialReviewsCap% for dynamic values)"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="reviewsLimit-cta" className="block text-sm font-medium">
                  CTA Text
                </label>
                <Input
                  id="reviewsLimit-cta"
                  value={newMetadata.copy?.TRIAL_LIMIT_REVIEWS_COUNT?.ctaText || ''}
                  onChange={e =>
                    setNewMetadata({
                      ...newMetadata,
                      copy: {
                        ...newMetadata.copy!,
                        TRIAL_LIMIT_REVIEWS_COUNT: {
                          ...newMetadata.copy!.TRIAL_LIMIT_REVIEWS_COUNT,
                          ctaText: e.target.value,
                        },
                      },
                    })
                  }
                  placeholder="Enter CTA button text"
                />
              </div>
            </CardContent>
          </Card>

          {/* NO_SUBSCRIPTION */}
          <Card>
            <CardContent className="space-y-4 p-6">
              <h3 className="text-lg font-semibold">No Subscription Modal</h3>

              <div className="space-y-2">
                <label htmlFor="noSub-title" className="block text-sm font-medium">
                  Title
                </label>
                <Input
                  id="noSub-title"
                  value={newMetadata.copy?.NO_SUBSCRIPTION.title || ''}
                  onChange={e =>
                    setNewMetadata({
                      ...newMetadata,
                      copy: {
                        ...newMetadata.copy!,
                        NO_SUBSCRIPTION: {
                          ...newMetadata.copy!.NO_SUBSCRIPTION,
                          title: e.target.value,
                        },
                      },
                    })
                  }
                  placeholder="Enter modal title"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="noSub-body" className="block text-sm font-medium">
                  Body
                </label>
                <Input
                  id="noSub-body"
                  value={newMetadata.copy?.NO_SUBSCRIPTION.body || ''}
                  onChange={e =>
                    setNewMetadata({
                      ...newMetadata,
                      copy: {
                        ...newMetadata.copy!,
                        NO_SUBSCRIPTION: {
                          ...newMetadata.copy!.NO_SUBSCRIPTION,
                          body: e.target.value,
                        },
                      },
                    })
                  }
                  placeholder="Enter modal body"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="noSub-cta" className="block text-sm font-medium">
                  CTA Text
                </label>
                <Input
                  id="noSub-cta"
                  value={newMetadata.copy?.NO_SUBSCRIPTION.ctaText || ''}
                  onChange={e =>
                    setNewMetadata({
                      ...newMetadata,
                      copy: {
                        ...newMetadata.copy!,
                        NO_SUBSCRIPTION: {
                          ...newMetadata.copy!.NO_SUBSCRIPTION,
                          ctaText: e.target.value,
                        },
                      },
                    })
                  }
                  placeholder="Enter CTA button text"
                />
              </div>
            </CardContent>
          </Card>

          {/* PAID_LIMIT_EXPORT_COUNT */}
          <Card>
            <CardContent className="space-y-4 p-6">
              <h3 className="text-lg font-semibold">PAID plan - Daily Export Limit Modal</h3>

              <div className="space-y-2">
                <label htmlFor="noSub-title" className="block text-sm font-medium">
                  Title
                </label>
                <Input
                  id="noSub-title"
                  value={newMetadata.copy?.PAID_LIMIT_EXPORT_COUNT?.title || defaultCopy.PAID_LIMIT_EXPORT_COUNT.title}
                  onChange={e =>
                    setNewMetadata({
                      ...newMetadata,
                      copy: {
                        ...newMetadata.copy!,
                        PAID_LIMIT_EXPORT_COUNT: {
                          ...newMetadata.copy!.PAID_LIMIT_EXPORT_COUNT,
                          title: e.target.value,
                        },
                      },
                    })
                  }
                  placeholder="Enter modal title"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="noSub-body" className="block text-sm font-medium">
                  Body
                </label>
                <Input
                  id="noSub-body"
                  value={newMetadata.copy?.PAID_LIMIT_EXPORT_COUNT?.body || defaultCopy.PAID_LIMIT_EXPORT_COUNT.body}
                  onChange={e =>
                    setNewMetadata({
                      ...newMetadata,
                      copy: {
                        ...newMetadata.copy!,
                        PAID_LIMIT_EXPORT_COUNT: {
                          ...newMetadata.copy!.PAID_LIMIT_EXPORT_COUNT,
                          body: e.target.value,
                        },
                      },
                    })
                  }
                  placeholder="Enter modal body"
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <div className="flex gap-3 border-t pt-6">
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </>
  )
}
